import { useState } from "react";
import { toast } from "sonner";
import { Calculator, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResourceTable } from "@/components/data/ResourceTable";
import { SimpleTable } from "@/components/data/SimpleTable";
import { MotifDialog } from "@/components/data/Dialogs";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, yesterday } from "@/lib/api";

const TYPES = { revendeur: "Revendeur", superviseur: "Superviseur", kgd: "KGD" };

function CalculDialog({ open, onClose, onDone }) {
  const { can } = useAuth();
  const [date, setDate] = useState(yesterday());
  const [p, setP] = useState(null);
  const apercu = async () => {
    try {
      setP((await api.post("/commissions/periodes/apercu", { date })).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const valider = async () => {
    try {
      await api.post("/commissions/periodes", { date });
      toast.success("Commissions de la semaine validées");
      setP(null);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const blocked = p && (p.deja_calculee || p.journees_ouvertes > 0 || !p.nb_lignes);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setP(null); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="commission-calcul-dialog">
        <DialogHeader>
          <DialogTitle>Calcul hebdomadaire des commissions</DialogTitle>
          <DialogDescription>Semaine du lundi au dimanche contenant la date choisie. Taux issus des paramètres. La commission revendeur déjà déduite au TFJ n'est pas payée une seconde fois.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-end">
          <div><Label>Date dans la semaine</Label><Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setP(null); }} data-testid="commission-date-input" /></div>
          <Button variant="outline" onClick={apercu} data-testid="commission-apercu-button"><Calculator className="h-4 w-4 mr-1" />Aperçu</Button>
        </div>
        {p && (
          <>
            <div className="text-xs space-y-1" data-testid="commission-apercu-info">
              <div>Période {fmtDate(p.debut)} → {fmtDate(p.fin)} · {p.nb_lignes} ligne(s) TFJ · Taux : revendeur {p.taux.revendeur} %, superviseur {p.taux.superviseur} %, KGD {p.taux.kgd} %</div>
              {p.deja_calculee && <div className="text-primary font-semibold">Semaine déjà calculée et validée — double calcul impossible.</div>}
              {p.journees_ouvertes > 0 && <div className="text-primary font-semibold">{p.journees_ouvertes} journée(s) non clôturée(s) dans la période.</div>}
            </div>
            <SimpleTable title="Aperçu par bénéficiaire" testId="commission-apercu-table" rows={p.rows.map((r) => ({ ...r, type: TYPES[r.beneficiaire_type] }))} cols={[
              { k: "type", label: "Type" }, { k: "libelle", label: "Bénéficiaire" }, { k: "base", label: "CA de base", type: "money" },
              { k: "montant_calcule", label: "Calculé", type: "money" }, { k: "montant_deduit", label: "Déjà déduit TFJ", type: "money" },
              { k: "net_a_payer", label: "Net à payer", type: "money" }]} />
          </>
        )}
        <DialogFooter>
          <Button onClick={valider} disabled={!p || blocked || !can("commissions", "cloturer")} data-testid="commission-valider-button">Valider les commissions de la semaine</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CommissionsPage() {
  const { can } = useAuth();
  const [calc, setCalc] = useState(false);
  const [cancel, setCancel] = useState(null);
  const [reload, setReload] = useState(0);
  const refresh = () => setReload((n) => n + 1);
  const extra = (row) => [{ key: "annuler-periode", label: "Annuler la période", icon: XCircle, show: can("commissions", "annuler") && row.statut === "validee", onClick: setCancel }];
  return (
    <div>
      <PageHeader title="Commissions hebdomadaires" subtitle="Revendeur, superviseur et KGD calculés sur le CA TFJ de la semaine. Calculé ≠ payé : chaque bénéficiaire a son état de paiement (Payer = sortie de trésorerie).">
        {can("commissions", "voir") && <Button size="sm" onClick={() => setCalc(true)} data-testid="commission-calcul-button"><Calculator className="h-4 w-4 mr-1" />Calculer une semaine</Button>}
      </PageHeader>
      <Tabs defaultValue="periodes">
        <TabsList className="mb-4"><TabsTrigger value="periodes" data-testid="tab-periodes">Périodes</TabsTrigger><TabsTrigger value="commissions" data-testid="tab-commissions">Commissions par bénéficiaire</TabsTrigger></TabsList>
        <TabsContent value="periodes"><ResourceTable res="commission_periodes" extraActions={extra} reloadKey={reload} /></TabsContent>
        <TabsContent value="commissions"><ResourceTable res="commissions" reloadKey={reload} /></TabsContent>
      </Tabs>
      <CalculDialog open={calc} onClose={() => setCalc(false)} onDone={refresh} />
      <MotifDialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)} title={`Annuler les commissions de la semaine du ${cancel ? fmtDate(cancel.debut) : ""}`}
        description="Possible uniquement si aucune commission de la période n'a été payée." confirmLabel="Annuler la période"
        onConfirm={async (motif) => { await api.post(`/commissions/periodes/${cancel.id}/annuler`, { motif }); toast.success("Période annulée"); refresh(); }} />
    </div>
  );
}
